// src/components/ui/button.jsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap rounded-lg",
    "text-sm font-medium transition-colors duration-200",
    "[&>svg]:h-4 [&>svg]:w-4",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50",
    "disabled:opacity-40 disabled:cursor-not-allowed",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-slate-900 text-white hover:bg-slate-800",
        secondary:
          "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300",
        outline:
          "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300",
        subtle:
          "bg-slate-100 text-slate-700 hover:bg-slate-200",
        ghost:
          "bg-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100",
        destructive:
          "bg-red-600 text-white hover:bg-red-700",
        destructiveOutline:
          "bg-white text-red-600 border border-red-200 hover:bg-red-50",
        accent:
          "bg-indigo-600 text-white hover:bg-indigo-700",
        accentOutline:
          "bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50",
        link: "bg-transparent text-indigo-600 underline-offset-4 hover:underline",
      },
      size: {
        default: "px-4 py-2 rounded-lg",
        sm: "h-8 px-3 text-xs rounded-lg",
        lg: "h-10 px-5 text-base rounded-lg",
        icon: "p-2 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, children, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    // Detect if button has icon + label (2 children)
    const count =
      React.Children.toArray(children).filter(Boolean).length;

    const needsGap = count > 1 && size !== "icon";

    // NOTE: For icon-only buttons, always pass an accessible `aria-label`.

    return (
      <Comp
        ref={ref}
        type={Comp === "button" ? type || "button" : type}
        className={cn(
          buttonVariants({ variant, size }),
          needsGap && "gap-2", // ✅ Only here
          className
        )}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
